import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  FC,
} from "react";
import { IMachine } from "../types/Machine";
import {
  getMachineByUuid,
  checkMachineExists,
} from "../repositories/PredsenseRepository";
import {
  localStorageGet,
  localStorageSet,
  localStorageRemove,
} from "../utils/LocalStorageHelper";

interface MachineContextType {
  machine: IMachine | null;
  machineUuid: string | null;
  setMachineUuid: (uuid: string) => void;
  clearMachine: () => void;
}

const MachineContext = createContext<MachineContextType>({
  machine: null,
  machineUuid: null,
  setMachineUuid: () => {},
  clearMachine: () => {},
});

export const MachineProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [machineUuid, setMachineUuidState] = useState<string | null>(() => {
    const storedMachineUuid = localStorageGet("machine");
    return storedMachineUuid || null;
  });
  const [machine, setMachine] = useState<IMachine | null>(null);

  const setMachineUuid = async (uuid: string) => {
    const exists = await checkMachineExists(uuid);
    if (!exists) {
      clearMachine();
      return;
    }
    setMachineUuidState(uuid);
    localStorageSet("machine", uuid);
  };

  const clearMachine = () => {
    setMachine(null);
    setMachineUuidState(null);
    localStorageRemove("machine");
  };

  useEffect(() => {
    if (!!machineUuid) {
      getMachineByUuid(machineUuid)
        .then((data) => {
          setMachine(data);
        })
        .catch(() => {
          clearMachine();
        });
    }
  }, [machineUuid]);

  return (
    <MachineContext.Provider
      value={{ machine, machineUuid, setMachineUuid, clearMachine }}
    >
      {children}
    </MachineContext.Provider>
  );
};

export const useMachine = () => {
  const context = useContext(MachineContext);
  if (context === undefined) {
    throw new Error("useMachine must be used within a MachineProvider");
  }
  return context;
};
